/* eslint max-len: 0 */

import i18next from 'i18next';
import { AppIntegrationsRepository, DevicesRepository } from 'common/repositories/index';
import Alert from 'directives/dashboard/ui-alert/-RFC.ui-alert-logic';
import Mixpanel from 'common/services/stats/-RFC.Mixpanel';
import Util from 'common/services/util/-RFC.util';
import { FilterDeviceName, FilterImageUrlParse } from 'common/filters/index';

/**
* @ngdoc service
* @name notifications.AppNotifications
*
* @description
* This class is used to process notifications on apps.
*/
const AppNotifications = {

    /**
     * @ngdoc function
     * @name notifications.AppNotifications#processUpdate
     * @methodOf notifications.AppNotifications
     * @function
     *
     * @description
     * This function can be used to process updates made on a app.
     *
     * @param {object} data an object with updated values on app.
    */
    processUpdate(data, instance) {

        const device = DevicesRepository.getById(Util.deepFind(instance, 'relationships.device.data.id'));
        const appIntegration = AppIntegrationsRepository
            .getById(instance.relationships.app.data.id);

        if (device) {

            if (data['attributes.state'] === 'active') {

                Mixpanel.send('ConnectWeb Alert Triggered', {
                    'Alert Type': 'Success',
                    'Alert Name': `${Util.deepFind(appIntegration, 'attributes.resource.oeName')} Installed`,
                });

                Alert.success({
                    message: 'cw.app.install.success',
                    icon: {
                        cssClasses: `icon-${Util.deepFind(device, 'attributes.type')}`,
                    },
                }, {
                    message: {
                        appName: Util.deepFind(appIntegration, 'attributes.resource.oeName'),
                        deviceName: FilterDeviceName(device),
                    },
                });

            }

            // Antivirus module(s) deactivated
            const deactivatedModules = this._getDeactivatedModules(data);
            if (deactivatedModules.length === 1) {

                // module was deactivated
                Alert.warning(
                    {
                        message: 'cw.service.antivirus.module.off',
                        icon: {
                            cssClasses: 'icon-warning-filled',
                        },
                    },
                    {
                        message: {
                            deviceName: FilterDeviceName(device),
                            moduleName: deactivatedModules[0],
                        },
                    },
                );

            } else if (deactivatedModules.length > 1) {

                // modules were deactivated
                Alert.warning(
                    {
                        message: 'cw.service.antivirus.modules.off',
                        icon: {
                            cssClasses: 'icon-warning-filled',
                        },
                    },
                    {
                        message: {
                            deviceName: FilterDeviceName(device),
                            modulesNames: deactivatedModules.join(', '),
                        },
                    },
                );

            }

            // Antivirus module(s) activated
            const activatedModules = this._getActivatedModules(data);
            if (activatedModules.length === 1) {

                // module was activated
                Alert.info(
                    {
                        message: 'cw.service.antivirus.module.on',
                        icon: {
                            cssClasses: 'icon-info',
                        },
                    },
                    {
                        message: {
                            deviceName: FilterDeviceName(device),
                            moduleName: activatedModules[0],
                        },
                    },
                );

            } else if (activatedModules.length > 1) {

                // modules were activated
                Alert.info(
                    {
                        message: 'cw.service.antivirus.modules.on',
                        icon: {
                            cssClasses: 'icon-info',
                        },
                    },
                    {
                        message: {
                            deviceName: FilterDeviceName(device),
                            modulesNames: activatedModules.join(', '),
                        },
                    },
                );

            }

            // Antivirus modules are activated
            if (data['attributes.app_status'] === 'protected') {

                // all modules were activated
                Alert.success(
                    {
                        message: 'cw.service.antivirus.secured',
                        icon: {
                            imageUrl: FilterImageUrlParse(appIntegration.attributes.resource.wsLargeImageUrl),
                        },
                    },
                    {
                        message: {
                            deviceName: FilterDeviceName(device),
                        },
                    },
                );

            }

        }

    },

    _getDeactivatedModules(data) {

        const deactivatedModules = [];

        if (data['attributes.status.svc.guard'] &&
            (+data['attributes.status.svc.guard'] === 2 ||
                +data['attributes.status.svc.guard'] === 4)) {

            // guard was deactivated
            deactivatedModules.push(i18next.t('cw.dashboard.services.windows.antivirus.guard.name'));

        }

        if (data['attributes.status.svc.firewall'] &&
            (+data['attributes.status.svc.firewall'] === 2 ||
                +data['attributes.status.svc.firewall'] === 4)) {

            // guard was deactivated
            deactivatedModules.push(i18next.t('cw.dashboard.services.windows.antivirus.firewall.name'));

        }

        if (data['attributes.status.svc.mailguard'] &&
            (+data['attributes.status.svc.mailguard'] === 2 ||
                +data['attributes.status.svc.mailguard'] === 4)) {

            // mailguard was deactivated
            deactivatedModules.push(i18next.t('cw.dashboard.services.windows.antivirus.mailguard.name'));

        }

        if (data['attributes.status.svc.webguard'] &&
            (+data['attributes.status.svc.webguard'] === 2 ||
                +data['attributes.status.svc.webguard'] === 4)) {

            // webguard was deactivated
            deactivatedModules.push(i18next.t('cw.dashboard.services.windows.antivirus.webguard.name'));

        }

        return deactivatedModules;

    },

    _getActivatedModules(data) {

        const activatedModules = [];

        if (data['attributes.status.svc.guard'] &&
            +data['attributes.status.svc.guard'] === 3) {

            // guard was activated
            activatedModules.push(i18next.t('cw.dashboard.services.windows.antivirus.guard.name'));

        }

        if (data['attributes.status.svc.firewall'] &&
            +data['attributes.status.svc.firewall'] === 3) {

            // guard was activated
            activatedModules.push(i18next.t('cw.dashboard.services.windows.antivirus.firewall.name'));

        }

        if (data['attributes.status.svc.mailguard'] &&
            +data['attributes.status.svc.mailguard'] === 3) {

            // mailguard was activated
            activatedModules.push(i18next.t('cw.dashboard.services.windows.antivirus.mailguard.name'));

        }

        if (data['attributes.status.svc.webguard'] &&
            +data['attributes.status.svc.webguard'] === 3) {

            // webguard was activated
            activatedModules.push(i18next.t('cw.dashboard.services.windows.antivirus.webguard.name'));

        }

        return activatedModules;

    },

    /**
     * @ngdoc function
     * @name notifications.AppNotifications#processDelete
     * @methodOf notifications.AppNotifications
     * @function
     *
     * @description
     * This function can be used to process removed app.
     *
     * @param {object} instance removed app.
    */
    processDelete(instance) {

        const device = DevicesRepository.getById(Util.deepFind(instance, 'relationships.device.data.id'));
        const appIntegration = AppIntegrationsRepository
            .getById(instance.relationships.app.data.id);

        if (device) {

            Alert.info({
                message: 'cw.app.remove.success',
                icon: {
                    cssClasses: 'icon-remove',
                },
            }, {
                message: {
                    appName: Util.deepFind(appIntegration, 'attributes.resource.oeName'),
                    deviceName: FilterDeviceName(device),
                },
            });

        }

    },

};

export default AppNotifications;



// WEBPACK FOOTER //
// ./scripts/common/notifications/-RFC.appNotifications.js